import { TWITTER_AUTOMATION_STAGE_STATUSES } from './twitterAutomationStageStatuses'
import * as twitterUrls from './twitterUrls'
import { XSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { TwitterRecommendationKey } from '../../utils/enums'
import * as enums from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import {
  getCurrentValueForCheckbox,
  saveCurrentValueForCheckbox,
  toggleCheckbox,
} from '../checkboxDetection'
import {
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

// Settings property functions
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? 'Allow' : "Don't Allow"))

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => !value)

const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

// Constant associating each setting with expected label text

const DISCOVERABILITY_SETTINGS = {
  [XSettingKey.DISCOVER_EMAIL_ADDRESS]:
    'Let people who have your email address find you on X',
  [XSettingKey.DISCOVER_PHONE_NUMBER]:
    'Let people who have your phone number find you on X',
}

// Setup gather stage definitions

enum StageKey {
  GATHER_SETTING_DISCOVERABLE_EMAIL_ADDRESS = 'GATHER_SETTING_DISCOVERABLE_EMAIL_ADDRESS',
  GATHER_SETTING_DISCOVERABLE_PHONE_NUMBER = 'GATHER_SETTING_DISCOVERABLE_PHONE_NUMBER',
  UPDATE_SETTING_DISCOVERABLE_EMAIL_ADDRESS = 'UPDATE_SETTING_DISCOVERABLE_EMAIL_ADDRESS',
  UPDATE_SETTING_DISCOVERABLE_PHONE_NUMBER = 'UPDATE_SETTING_DISCOVERABLE_PHONE_NUMBER',
  UNDO_SETTING_DISCOVERABLE_EMAIL_ADDRESS = 'UNDO_SETTING_DISCOVERABLE_EMAIL_ADDRESS',
  UNDO_SETTING_DISCOVERABLE_PHONE_NUMBER = 'UNDO_SETTING_DISCOVERABLE_PHONE_NUMBER',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_DISCOVERABLE_EMAIL_ADDRESS,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: twitterUrls.contactsSettingsUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForCheckbox(
        userContext.updateStoredAutomationErrors,
        userContext.updateStoredInitialValues,
        DISCOVERABILITY_SETTINGS,
        XSettingKey.DISCOVER_EMAIL_ADDRESS,
        TwitterRecommendationKey.TWITTER_DISCOVERABILITY,
      )
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_DISCOVERABLE_PHONE_NUMBER,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: twitterUrls.contactsSettingsUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForCheckbox(
        userContext.updateStoredAutomationErrors,
        userContext.updateStoredInitialValues,
        DISCOVERABILITY_SETTINGS,
        XSettingKey.DISCOVER_PHONE_NUMBER,
        TwitterRecommendationKey.TWITTER_DISCOVERABILITY,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_DISCOVERABLE_EMAIL_ADDRESS,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: twitterUrls.contactsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.DISCOVER_EMAIL_ADDRESS]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForCheckbox(
        DISCOVERABILITY_SETTINGS[XSettingKey.DISCOVER_EMAIL_ADDRESS],
      )
      if (!currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        DISCOVERABILITY_SETTINGS[XSettingKey.DISCOVER_EMAIL_ADDRESS],
      )
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_DISCOVERABLE_PHONE_NUMBER,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: twitterUrls.contactsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.DISCOVER_PHONE_NUMBER]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForCheckbox(
        DISCOVERABILITY_SETTINGS[XSettingKey.DISCOVER_PHONE_NUMBER],
      )
      if (!currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        DISCOVERABILITY_SETTINGS[XSettingKey.DISCOVER_PHONE_NUMBER],
      )
      return null
    },
  },
]

// Execution code

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_DISCOVERABLE_EMAIL_ADDRESS,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: twitterUrls.contactsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.DISCOVER_EMAIL_ADDRESS]
      ) {
        return null
      }

      const prevValue = initialValues[XSettingKey.DISCOVER_EMAIL_ADDRESS]
      const currentValue = await getCurrentValueForCheckbox(
        DISCOVERABILITY_SETTINGS[XSettingKey.DISCOVER_EMAIL_ADDRESS],
      )
      if (prevValue === currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        DISCOVERABILITY_SETTINGS[XSettingKey.DISCOVER_EMAIL_ADDRESS],
      )
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_DISCOVERABLE_PHONE_NUMBER,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: twitterUrls.contactsSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[XSettingKey.DISCOVER_PHONE_NUMBER]
      ) {
        return null
      }

      const prevValue = initialValues[XSettingKey.DISCOVER_PHONE_NUMBER]
      const currentValue = await getCurrentValueForCheckbox(
        DISCOVERABILITY_SETTINGS[XSettingKey.DISCOVER_PHONE_NUMBER],
      )
      if (prevValue === currentValue) {
        return null
      }

      if (isDry) {
        return null
      }

      await toggleCheckbox(
        DISCOVERABILITY_SETTINGS[XSettingKey.DISCOVER_PHONE_NUMBER],
      )
      return null
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
